import { AuthOptions, createAuthLink } from 'aws-appsync-auth-link'
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link'
import fetch from 'cross-fetch'
import Cookies from 'js-cookie'

import {
   ApolloClient,
   ApolloLink,
   DefaultOptions,
   HttpLink,
   InMemoryCache
} from '@apollo/client'
import { Config } from '@flowwai-frontend/bps-officer-web/env-config'
import { AUTH_TYPE } from '@flowwai-frontend/shared/common'

import { customFieldMerger } from '../utils/TypePoliciesUtils'
import {
   getAccountAutomationWorkflowExecLogs,
   getAccountLeadDistributionRuleExecLogs,
   getAccountLeadDistributionRules,
   getAgentsForManager,
   getAllAWs,
   getDataExportRequests,
   getDataLoadingRequests,
   getDealEntityDetails,
   getFailedTrackActivityLogs,
   getGallaboxAccountPluginServiceAccounts,
   getGlobalSearchResults,
   getGupShupAccountPluginServiceAccounts,
   getIdentificationsInAccount,
   getPipelineEventBasedLeadDistributionExecLogs,
   getPipelineItemRemarksDetails,
   getRelatedObjects,
   getRelatedObjectsToLink,
   getPipelineItemMessageTemplates,
   getPipelineManualLeadDistributionExecLogs,
   getPipelineScheduledLeadDistributionExecLogs,
   getUserDashboards,
   getUsersInAccount,
   getWatiAccountPluginServiceAccounts,
   getWhatsappBusinessNumbers,
   searchActivityTemplates,
   searchIdentifications,
   searchLeadCreationAllowedStages,
   searchUserGroups,
   searchFields,
   getRelatedPipelineItemRemarks,
   getAccountScheduledAutomationWorkflowLogs,
   getUserNotifications,
   getDocumentHistory,
   getPipelineItemDocumentTemplates,
   getScoringRecalculationExecLogs,
   getPluginServiceAccountCallLogs,
   getPipelineItemResources,
   getEntityNotes,
   searchDocumentVerificationUsers,
   getOfficerManagementUsers,
   searchUsernames,
   getOfficerLeaveLogs,
   getOfficerManagementLogs,
   searchOfficerManagementUsers,
   getFeeRuleSetLogs,
   searchAndMonitoringApplications,
   getTgBPassApplications,
   getAssignedFriendApplications,
   getScrutinyKeyLogs
} from './mergeFunctions'

// TODO: Replace this to use env variables
const GRAPHQL_BASE_URL = Config.BACKEND_GRAPHQL_URL

export const url = GRAPHQL_BASE_URL
export const subscriptionUrl = Config.BACKEND_GRAPHQL_SUBSCRIPTION_URL
export const region = 'AP-SOUTH-1'
export const NETWORK_FETCH_POLICY = 'no-cache'
export const CACHE_FIRST_FETCH_POLICY = 'cache-first'
export const CACHE_AND_NETWORK_FETCH_POLICY = 'cache-and-network'
export const CACHE_ONLY_FETCH_POLICY = 'cache-only'
export const NETWORK_ONLY_FETCH_POLICY = 'network-only'

const auth: AuthOptions = {
   type: AUTH_TYPE.AWS_LAMBDA,
   //  TODO: change the access token fetching to common util once set up is done
   token: async () => (await `Bearer ${Cookies.get('access_token')}`) ?? '' // Required when you use Cognito UserPools OR OpenID Connect. token object is obtained previously
}

const httpLink = new HttpLink({ uri: url, fetch })

const link = ApolloLink.from([
   createAuthLink({ url, region, auth }),
   createSubscriptionHandshakeLink({ url, region: region, auth }, httpLink)
])

const defaultOptions: DefaultOptions = {
   query: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all'
   },
   mutate: {
      fetchPolicy: 'no-cache'
   },
   watchQuery: {
      fetchPolicy: 'no-cache'
   }
}

const cache = new InMemoryCache({
   possibleTypes: {
      BasePipelineItemProperties: [
         'LeadProperties',
         'DealProperties',
         'PipelineCustomObjectProperties',
         'ApplicationProperties'
      ],
      ItemDetail: ['Folder', 'Page'],
      GetSpaceResult: ['Space', 'SpacePermissionDenied', 'SpaceNotFound'],
      GetLeadRemindersForUserResponse: [
         'LeadRemindersResult',
         'InvalidLimit',
         'InvalidOffset'
      ],
      FieldResponseType: [
         'GQLStringType',
         'GQLIntType',
         'GQLFloatType',
         'GQLListStringType',
         'GQLDateType',
         'GQLTimeType',
         'GQLDateTimeType',
         'PhoneNumberResponse',
         'Stage',
         'StageNotFound',
         'StageIsDeleted',
         'BaseUser',
         'BasePipeline',
         'FileUploaderFieldResponse',
         'RelationFieldResponse',
         'GeoLocationFieldResponse',
         'GeoShapeFieldResponse',
         'ShortTextFieldResponse'
      ],
      Field: [
         'IntegerField',
         'FloatField',
         'EmailField',
         'FileUploaderField',
         'LongTextField',
         'PlainTextField',
         'ShortTextField',
         'URLField',
         'TimeField',
         'DateField',
         'DateTimeField',
         'DropdownField',
         'MultiSelectField',
         'CheckboxGroupField',
         'RadioGroupField',
         'PhoneNumberField',
         'FieldsSelectorField',
         'RelationField',
         'GeoLocationField',
         'GeoShapeField'
      ],
      FieldResult: [
         'IntegerField',
         'FloatField',
         'EmailField',
         'FileUploaderField',
         'LongTextField',
         'PlainTextField',
         'ShortTextField',
         'URLField',
         'TimeField',
         'DateField',
         'DateTimeField',
         'DropdownField',
         'MultiSelectField',
         'CheckboxGroupField',
         'RadioGroupField',
         'PhoneNumberField',
         'FieldsSelectorField',
         'RelationField',
         'FieldNotFound',
         'NotPermittedField',
         'FieldIsDeleted',
         'GeoLocationField',
         'GeoShapeField'
      ],
      LeadPropertiesResponse: [
         'LeadProperties',
         'LeadNotFound',
         'LeadAccessDenied'
      ],
      LeadsInViewResponse: [
         'LeadsInViewResult',
         'ViewNotFound',
         'ViewAccessDenied'
      ],
      ContactResult: ['Contact', 'ContactNotFound', 'ContactAccessDenied'],
      CreateEntityDealResponse: [
         'StageNotFound',
         'DealAssigneeNotFound',
         'DealEntityNotFound',
         'DealAssigneeIsDeactivated',
         'DealEntityAccessDenied',
         'PipelineAccessDenied',
         'NotDealPipelineStage',
         'PipelineAccessDeniedToAssignee',
         'NotAssigneeFieldIds',
         'DealAssigneesNotFound',
         'DealAssigneesAreDeactivated',
         'DuplicateFieldIds',
         'NotDealTemplateFields',
         'FieldsNotFound',
         'FieldsAreDeleted',
         'NeedFieldsWritePermission',
         'RequiredFieldsNotFilled',
         'Deal'
      ],
      PushContactToPipelineResponse: [
         'PushContactToPipelineResult',
         'ContactAccessDenied',
         'ContactNotFound',
         'StageNotFound',
         'PipelineAccessDenied',
         'ContactAndStageAreNotInSameAccount',
         'ContactAlreadyPushed',
         'NotLeadPipelineStage',
         'FieldsNotFound',
         'FieldsAreDeleted',
         'NotLeadAssigneeFields',
         'LeadAssigneesNotFound',
         'LeadAssigneesAreDeactivated',
         'PipelineAccessDeniedToAssignees',
         'YouDonNotHavePermissionToSetGivenUsersAsAssignees'
      ],
      UpdateDealAssigneeBulkResponse: [
         'UpdateDealAssigneeBulkResult',
         'FieldNotFound',
         'FieldIsDeleted',
         'DealsNotFound',
         'DealsAccessDenied',
         'NotDealAssigneeField',
         'NeedFieldWritePermission',
         'DealAssigneeNotFound',
         'PipelineAccessDeniedToAssignee',
         'YouDonNotHavePermissionToSetGivenUserAsAssignee',
         'AccountAccessDeniedToAssignee',
         'DealAssigneeIsDeactivated'
      ],
      UpdateLeadAssigneeBulkResponse: [
         'UpdateLeadAssigneeBulkResult',
         'FieldNotFound',
         'FieldIsDeleted',
         'LeadsNotFound',
         'LeadsAccessDenied',
         'NotLeadAssigneeField',
         'NeedFieldWritePermission',
         'LeadAssigneeNotFound',
         'PipelineAccessDeniedToAssignee',
         'YouDonNotHavePermissionToSetGivenUserAsAssignee',
         'AccountAccessDeniedToAssignee',
         'LeadAssigneeIsDeactivated'
      ],
      GetDealTemplateSystemFieldsResponse: [
         'DealTemplatesNotFound',
         'DealTemplatesAccessDenied',
         'DealTemplateSystemFieldsResult'
      ],
      GetLeadTemplateSystemFieldsResponse: [
         'LeadTemplatesNotFound',
         'LeadTemplatesAccessDenied',
         'LeadTemplateSystemFieldsResult'
      ],
      BasePipelineItemPrimaryDetails: [
         'LeadPrimaryDetails',
         'DealPrimaryDetails'
      ],
      PipelineItemPrimaryDetails: ['LeadPrimaryDetails', 'DealPrimaryDetails'],
      AnalyticsVisualizations: [
         'AnalyticsCoPilotTableData',
         'AnalyticsCoPilotScoreCardData',
         'AnalyticsBarGraph',
         'AnalyticsDonut',
         'AnalyticsPieChart',
         'AnalyticsLineGraph',
         'AnalyticsFunnelGraph'
      ],
      Order: ['PaymentOrder', 'InstallmentGroup'],
      ExtFieldResponseType: [
         'ExtGQLStringType',
         'ExtGQLIntType',
         'ExtGQLFloatType',
         'ExtGQLListStringType',
         'ExtGQLDateType',
         'ExtGQLTimeType',
         'ExtGQLDateTimeType',
         'ExtPhoneNumberResponse',
         'ExtStageResponse',
         'ExtFileUploaderFieldResponse',
         'ExtGeoLocationFieldResponse',
         'ExtGeoShapeFieldResponse',
         'ExtShortTextFieldResponse'
      ]
   },

   typePolicies: {
      Query: {
         fields: {
            getPipelineItemTasks: {
               keyArgs: ['params', ['pipelineItemId']],
               merge: (existing, incoming, { args }) => {
                  if (!existing) return incoming
                  if (args?.params.pagination.offset === 0) return incoming

                  const prevTasks = [...(existing.tasks || [])]

                  const newTasks = [...(incoming.tasks || [])]

                  const filterPrevTasks = prevTasks.filter(prevRem => {
                     const availableTask = newTasks.find(
                        newRem =>
                           // eslint-disable-next-line no-underscore-dangle
                           prevRem.__ref === newRem.__ref
                     )
                     return !availableTask
                  })

                  const finalTasks = [...filterPrevTasks, ...newTasks]

                  return {
                     ...existing,
                     tasks: finalTasks,
                     totalCount: incoming.totalCount
                  }
               }
            },
            getTasks: {
               keyArgs: ['params', ['taskIds']]
            },
            getPendingTaskOnPipelineItem: {
               keyArgs: ['params', ['taskTemplateId']]
            },
            getTaskTemplates: { keyArgs: ['params', ['taskTemplateIds']] },
            getEntityDeals: {
               keyArgs: ['params', ['dealEntityId']],
               merge: (existing, incoming, { args }) =>
                  getDealEntityDetails(existing, incoming, args)
            },
            searchIdentifications: {
               keyArgs: ['params', ['pipelineIds', 'searchQuery']],
               merge: (existing, incoming, { args }) =>
                  searchIdentifications(existing, incoming, args)
            },
            getUserAppConfig: {
               keyArgs: [],
               merge: true
            },
            getPipelineItemRemarks: {
               keyArgs: ['params', ['pipelineItemId']],
               merge: (existing, incoming, { args }) =>
                  getPipelineItemRemarksDetails(existing, incoming, args)
            },
            getPipelineItemActivities: {
               keyArgs: ['params', ['pipelineItemId']],
               merge: (existing, incoming, { args }) => {
                  if (!existing) return incoming
                  if (args?.params.pagination.offset === 0) return incoming

                  const existingActivities = [...(existing.activities || [])]

                  const incomingActivities = [...(incoming.activities || [])]

                  const filterExistingActivities = existingActivities.filter(
                     existingActivity => {
                        const availableActivity = incomingActivities.find(
                           incomingActivity =>
                              // eslint-disable-next-line no-underscore-dangle
                              existingActivity.__ref === incomingActivity.__ref
                        )
                        return !availableActivity
                     }
                  )

                  const finalActivities = [
                     ...filterExistingActivities,
                     ...incomingActivities
                  ]

                  return {
                     ...existing,
                     activities: finalActivities,
                     totalCount: incoming.totalCount
                  }
               }
            },

            getParametersInAccount: {
               keyArgs: ['params', ['parameterId']],
               merge: (existing, incoming, { args }) => {
                  if (!existing) return incoming
                  if (args?.params.pagination.offset === 0) return incoming

                  const existingParameters = [...(existing.parameters || [])]
                  const newParameters = [...(incoming.parameters || [])]

                  const filterPrevParameters = existingParameters.filter(
                     existingParameter => {
                        const availableParameters = newParameters.find(
                           newParameter =>
                              // eslint-disable-next-line no-underscore-dangle
                              existingParameter.__ref === newParameter.__ref
                        )
                        return !availableParameters
                     }
                  )

                  const finalParameters = [
                     ...filterPrevParameters,
                     ...newParameters
                  ]

                  return {
                     ...existing,
                     parameters: finalParameters,
                     totalCount: incoming.totalCount
                  }
               }
            },
            getLeadRemarksResult: {
               keyArgs: ['params', ['pipelineItemRemarksId']],
               merge: (existing, incoming, { args }) => {
                  if (!existing) return incoming
                  if (args?.params.pagination.offset === 0) return incoming

                  const existingRemarks = [...(existing.remarks || [])]
                  const newRemarks = [...(incoming.remarks || [])]

                  const filterPrevRemarks = existingRemarks.filter(
                     existingRemark => {
                        const availableRemarks = newRemarks.find(
                           newRemark =>
                              // eslint-disable-next-line no-underscore-dangle
                              existingRemark.__ref === newRemark.__ref
                        )
                        return !availableRemarks
                     }
                  )

                  const finalRemarks = [...filterPrevRemarks, ...newRemarks]

                  return {
                     ...existing,
                     remarks: finalRemarks,
                     totalCount: incoming.totalCount
                  }
               }
            },

            getContactLeads: {
               keyArgs: ['params', ['contactId']]
            },
            getTemplatesForPipelines: {
               keyArgs: ['params', ['pipelineIds']]
            },
            getPipelineItemCallLogs: {
               keyArgs: ['params', ['pipelineItemId']],
               merge: (existing, incoming, { args }) => {
                  if (!existing) return incoming
                  if (args?.params.pagination.offset === 0) return incoming

                  const existingCallLogs = [...(existing.callLogs || [])]

                  const incomingCallLogs = [...(incoming.callLogs || [])]

                  const filterExistingCallLogs = existingCallLogs.filter(
                     existingCallLog => {
                        const availableCallLog = incomingCallLogs.find(
                           incomingCallLog =>
                              // eslint-disable-next-line no-underscore-dangle
                              existingCallLog.__ref === incomingCallLog.__ref
                        )
                        return !availableCallLog
                     }
                  )

                  const finalCallLogs = [
                     ...filterExistingCallLogs,
                     ...incomingCallLogs
                  ]

                  return {
                     ...existing,
                     callLogs: finalCallLogs,
                     totalCount: incoming.totalCount
                  }
               }
            },
            getPipelineItemHistory: {
               keyArgs: ['params', ['pipelineItemId']],
               merge: (existing, incoming, { args }) => {
                  if (!existing) return incoming
                  if (args?.params.pagination.offset === 0) return incoming

                  const existingHistoryLogs = [...(existing.history || [])]

                  const incomingHistoryLogs = [...(incoming.history || [])]

                  const filterExistingHistoryLogs = existingHistoryLogs.filter(
                     existingLog => {
                        const availableLog = incomingHistoryLogs.find(
                           incomingLog =>
                              // eslint-disable-next-line no-underscore-dangle
                              existingLog.__ref === incomingLog.__ref
                        )
                        return !availableLog
                     }
                  )

                  const finalHistoryLogs = [
                     ...filterExistingHistoryLogs,
                     ...incomingHistoryLogs
                  ]

                  return {
                     ...existing,
                     history: finalHistoryLogs,
                     totalCount: incoming.totalCount
                  }
               }
            },

            getAvailableCallLogsForTask: {
               keyArgs: ['params', ['pipelineItemId']],
               merge: (existing, incoming, { args }) => {
                  if (!existing) return incoming
                  if (args?.params.pagination.offset === 0) return incoming

                  const existingCallLogs = [...(existing.callLogs || [])]

                  const incomingCallLogs = [...(incoming.callLogs || [])]

                  const filterExistingCallLogs = existingCallLogs.filter(
                     existingCallLog => {
                        const availableCallLog = incomingCallLogs.find(
                           incomingCallLog =>
                              // eslint-disable-next-line no-underscore-dangle
                              existingCallLog.__ref === incomingCallLog.__ref
                        )
                        return !availableCallLog
                     }
                  )

                  const finalCallLogs = [
                     ...filterExistingCallLogs,
                     ...incomingCallLogs
                  ]

                  return {
                     ...existing,
                     callLogs: finalCallLogs,
                     totalCount: incoming.totalCount
                  }
               }
            },
            getPipelinesAutomationWorkflows: {
               keyArgs: ['params', ['limit']],
               merge: (existing, incoming, { args }) =>
                  getAllAWs(existing, incoming, args)
            },
            getRemindersOnPipelineItem: {
               keyArgs: ['params', ['pipelineItemId']],
               merge: (existing, incoming, { args }) => {
                  if (!existing) return incoming
                  if (args?.params.pagination.offset === 0) return incoming

                  const existingReminders = [...(existing.reminders || [])]

                  const incomingReminders = [...(incoming.reminders || [])]

                  const filterExistingReminders = existingReminders.filter(
                     existingReminder => {
                        const availableReminder = incomingReminders.find(
                           incomingReminder =>
                              // eslint-disable-next-line no-underscore-dangle
                              existingReminder.__ref === incomingReminder.__ref
                        )
                        return !availableReminder
                     }
                  )

                  const finalReminders = [
                     ...filterExistingReminders,
                     ...incomingReminders
                  ]

                  return {
                     ...existing,
                     reminders: finalReminders,
                     totalCount: incoming.totalCount
                  }
               }
            },
            getAgentAllocationGroupsInAccount: {
               keyArgs: ['params', ['searchQuery']],
               merge: (existing, incoming, { args }) => {
                  if (!existing) return incoming
                  if (args?.params.pagination.offset === 0) return incoming

                  const existingList = [
                     ...(existing.agentAllocationGroups || [])
                  ]
                  const incomingList = [
                     ...(incoming.agentAllocationGroups || [])
                  ]

                  const filteredExistingList = existingList.filter(
                     existingItem => {
                        const availableItem = incomingList.find(
                           incomingItem =>
                              // eslint-disable-next-line no-underscore-dangle
                              existingItem.__ref === incomingItem.__ref
                        )
                        return !availableItem
                     }
                  )
                  const finalList = [...filteredExistingList, ...incomingList]

                  return {
                     ...existing,
                     agentAllocationGroups: finalList,
                     totalCount: incoming.totalCount
                  }
               }
            },
            getPipelineItemRemindersForUser: {
               keyArgs: ['params', ['remindersDate']],
               merge: (existing, incoming, { args }) => {
                  if (!existing) return incoming
                  if (args?.params.pagination.offset === 0) return incoming

                  const existingReminders = [...(existing.reminders || [])]

                  const incomingReminders = [...(incoming.reminders || [])]

                  const filterExistingReminders = existingReminders.filter(
                     existingReminder => {
                        const availableReminder = incomingReminders.find(
                           incomingReminder =>
                              // eslint-disable-next-line no-underscore-dangle
                              existingReminder.__ref === incomingReminder.__ref
                        )
                        return !availableReminder
                     }
                  )

                  const finalReminders = [
                     ...filterExistingReminders,
                     ...incomingReminders
                  ]

                  return {
                     ...existing,
                     reminders: finalReminders,
                     totalCount: incoming.totalCount
                  }
               }
            },
            getAccountLeadDistributionRules: {
               keyArgs: ['params', ['leadDistributionRuleId', 'pipelineIds']],
               merge: (existing, incoming, { args }) =>
                  getAccountLeadDistributionRules(existing, incoming, args)
            },
            getAccountLeadDistributionRuleExecLogs: {
               keyArgs: ['params', ['leadDistributionRuleExecLogId']],
               merge: (existing, incoming, { args }) =>
                  getAccountLeadDistributionRuleExecLogs(
                     existing,
                     incoming,
                     args
                  )
            },
            getUserDashboards: {
               keyArgs: [
                  'params',
                  ['searchQuery', 'filterByIdentificationIds', 'pipelineId']
               ],
               merge: (existing, incoming, { args }) =>
                  getUserDashboards(existing, incoming, args)
            },
            searchUserGroups: {
               keyArgs: ['params', ['userGroupId']],
               merge: (existing, incoming, { args }) =>
                  searchUserGroups(existing, incoming, args)
            },
            getAccountAutomationWorkflowExecLogs: {
               keyArgs: ['params', ['execLogsTab']],
               merge: (existing, incoming, { args }) =>
                  getAccountAutomationWorkflowExecLogs(existing, incoming, args)
            },
            getDataExportRequests: {
               keyArgs: ['params', ['dataExportRequestId', 'pipelineId']],
               merge: (existing, incoming, { args }) =>
                  getDataExportRequests(existing, incoming, args)
            },
            getFailedTrackActivityLogs: {
               keyArgs: ['params', ['activityLogId', 'pipelineId']],
               merge: (existing, incoming, { args }) =>
                  getFailedTrackActivityLogs(existing, incoming, args)
            },
            searchActivityTemplates: {
               keyArgs: ['params', ['referenceId']],
               merge: (existing, incoming, { args }) =>
                  searchActivityTemplates(existing, incoming, args)
            },
            searchPipelineItems: {
               keyArgs: [
                  'params',
                  [
                     'pipelineId',
                     'searchQuery',
                     'excludePipelineItemIds',
                     'fieldIds'
                  ]
               ],
               merge: (existing, incoming, { args }) =>
                  getGlobalSearchResults(existing, incoming, args)
            },
            getUsersInAccount: {
               keyArgs: ['params', ['userId']],
               merge: (existing, incoming, { args }) =>
                  getUsersInAccount(existing, incoming, args)
            },
            getIdentificationsInAccount: {
               keyArgs: ['params', ['identificationId']],
               merge: (existing, incoming, { args }) =>
                  getIdentificationsInAccount(existing, incoming, args)
            },
            searchLeadCreationAllowedStages: {
               keyArgs: ['params', ['stageId']],
               merge: (existing, incoming, { args }) =>
                  searchLeadCreationAllowedStages(existing, incoming, args)
            },
            searchFields: {
               keyArgs: ['params', ['salesTemplateId']],
               merge: (existing, incoming, { args }) =>
                  searchFields(existing, incoming, args)
            },
            getDataLoadingRequests: {
               keyArgs: ['params', ['dataLoadingRequestId']],
               merge: (existing, incoming, { args }) =>
                  getDataLoadingRequests(existing, incoming, args)
            },
            getRelatedObjects: {
               keyArgs: ['params', ['relationId', 'currentEntityId']],
               merge: (existing, incoming, { args }) =>
                  getRelatedObjects(existing, incoming, args)
            },
            getRelatedObjectsToLink: {
               keyArgs: [
                  'params',
                  ['relationId', 'currentEntityId', 'searchQuery']
               ],
               merge: (existing, incoming, { args }) =>
                  getRelatedObjectsToLink(existing, incoming, args)
            },
            getPipelineManualLeadDistributionExecLogs: {
               keyArgs: ['params', ['pipelineId']],
               merge: (existing, incoming, { args }) =>
                  getPipelineManualLeadDistributionExecLogs(
                     existing,
                     incoming,
                     args
                  )
            },
            getPipelineEventBasedLeadDistributionExecLogs: {
               keyArgs: ['params', ['pipelineId']],
               merge: (existing, incoming, { args }) =>
                  getPipelineEventBasedLeadDistributionExecLogs(
                     existing,
                     incoming,
                     args
                  )
            },
            getPipelineScheduledLeadDistributionExecLogs: {
               keyArgs: ['params', ['pipelineId']],
               merge: (existing, incoming, { args }) =>
                  getPipelineScheduledLeadDistributionExecLogs(
                     existing,
                     incoming,
                     args
                  )
            },
            getCloudCallingUserConfigs: {
               keyArgs: ['params', ['userId']]
            },
            getWatiAccountPluginServiceAccounts: {
               keyArgs: ['params', ['pluginServiceAccountId']],
               merge: (existing, incoming, { args }) =>
                  getWatiAccountPluginServiceAccounts(existing, incoming, args)
            },
            getWhatsappBusinessNumbers: {
               keyArgs: ['params', ['pipelineWhatsappBusinessPhoneNumberId']],
               merge: (existing, incoming, { args }) =>
                  getWhatsappBusinessNumbers(existing, incoming, args)
            },
            getPipelineItemMessageTemplates: {
               keyArgs: ['params', ['pipelineItemId', 'searchQuery']],
               merge: (existing, incoming, { args }) =>
                  getPipelineItemMessageTemplates(existing, incoming, args)
            },
            getPipelineWhatsappMessageTemplates: {
               keyArgs: ['params', ['pipelineId', 'searchQuery']],
               merge: (existing, incoming, { args }) =>
                  getPipelineItemMessageTemplates(existing, incoming, args)
            },
            getGallaboxAccountPluginServiceAccounts: {
               keyArgs: ['params', ['pluginServiceAccountId']],
               merge: (existing, incoming, { args }) =>
                  getGallaboxAccountPluginServiceAccounts(
                     existing,
                     incoming,
                     args
                  )
            },
            getGupShupAccountPluginServiceAccounts: {
               keyArgs: ['params', ['accountPluginId']],
               merge: (existing, incoming, { args }) =>
                  getGupShupAccountPluginServiceAccounts(
                     existing,
                     incoming,
                     args
                  )
            },
            getAgentsForManager: {
               keyArgs: ['params', ['managerId']],
               merge: (existing, incoming, { args }) =>
                  getAgentsForManager(existing, incoming, args)
            },
            getRelatedPipelineItemRemarks: {
               keyArgs: ['params', ['relatedEntityId']],
               merge: (existing, incoming, { args }) =>
                  getRelatedPipelineItemRemarks(existing, incoming, args)
            },
            getAccountScheduledAutomationWorkflowLogs: {
               keyArgs: ['params', ['pipelineId']],
               merge: (existing, incoming, { args }) =>
                  getAccountScheduledAutomationWorkflowLogs(
                     existing,
                     incoming,
                     args
                  )
            },
            getUserNotifications: {
               keyArgs: ['params', ['userId']],
               merge: (existing, incoming, { args }) =>
                  getUserNotifications(existing, incoming, args)
            },
            getDocumentHistory: {
               keyArgs: ['params', ['documentId']],
               merge: (existing, incoming, { args }) =>
                  getDocumentHistory(existing, incoming, args)
            },
            getPipelineItemDocumentTemplates: {
               keyArgs: ['params', ['pipelineItemId']],
               merge: (existing, incoming, { args }) =>
                  getPipelineItemDocumentTemplates(existing, incoming, args)
            },
            getCompletedTaskFieldsResponse: {
               keyArgs: ['params', ['taskIds']]
            },
            getScoringRecalculationExecLogs: {
               keyArgs: ['params', ['pipelineId']],
               merge: (existing, incoming, { args }) =>
                  getScoringRecalculationExecLogs(existing, incoming, args)
            },
            getPluginServiceAccountCallLogs: {
               keyArgs: ['params', ['pluginServiceAccountId']],
               merge: (existing, incoming, { args }) =>
                  getPluginServiceAccountCallLogs(existing, incoming, args)
            },
            getAssociatedPipelinesForPipelineItemTemplates: {
               keyArgs: ['params', ['pipelineItemTemplateIds']]
            },
            getPipelineItemResources: {
               keyArgs: ['params', ['pipelineItemId']],
               merge: (existing, incoming, { args }) =>
                  getPipelineItemResources(existing, incoming, args)
            },
            getEntityNotes: {
               keyArgs: ['params', ['entityId']],
               merge: (existing, incoming, { args }) =>
                  getEntityNotes(existing, incoming, args)
            },
            getScrutinyKeyLogs: {
               keyArgs: ['params', ['applicationId', 'offset']],
               merge: (existing, incoming, { args }) =>
                  getScrutinyKeyLogs(existing, incoming, args)
            },
            searchDocumentVerificationUsers: {
               keyArgs: ['params', ['tabId', 'buttonId', 'searchQuery']],
               merge: (existing, incoming, { args }) =>
                  searchDocumentVerificationUsers(existing, incoming, args)
            },
            getOfficerManagementUsers: {
               keyArgs: ['params', ['searchQuery']],
               merge: (existing, incoming, { args }) =>
                  getOfficerManagementUsers(existing, incoming, args)
            },
            searchUsernames: {
               keyArgs: ['params', ['searchQuery']],
               merge: (existing, incoming, { args }) =>
                  searchUsernames(existing, incoming, args)
            },
            getOfficerLeaveLogs: {
               keyArgs: ['params', ['searchQuery']],
               merge: (existing, incoming, { args }) =>
                  getOfficerLeaveLogs(existing, incoming, args)
            },
            getOfficerManagementLogs: {
               keyArgs: [],
               merge: (existing, incoming, { args }) =>
                  getOfficerManagementLogs(existing, incoming, args)
            },
            searchOfficerManagementUsers: {
               keyArgs: ['params', ['searchQuery']],
               merge: (existing, incoming, { args }) =>
                  searchOfficerManagementUsers(existing, incoming, args)
            },
            getFeeRuleSetLogs: {
               keyArgs: ['params', ['entityFeeRuleSetIds', 'pipelineItemId']],
               merge: (existing, incoming, { args }) =>
                  getFeeRuleSetLogs(existing, incoming, args)
            },
            getTgBPassApplications: {
               keyArgs: false,
               merge: (existing, incoming, { args }) =>
                  getTgBPassApplications(existing, incoming, args)
            },
            searchAndMonitoringApplications: {
               keyArgs: ['params', ['conditions']],
               merge: (existing, incoming, { args }) =>
                  searchAndMonitoringApplications(existing, incoming, args)
            },
            getAssignedFriendApplications: {
               keyArgs: [],
               merge: (existing, incoming, { args }) =>
                  getAssignedFriendApplications(existing, incoming, args)
            },
            getEntityJsonConfiguration: {
               keyArgs: ['params', ['entityId', 'entityType']]
            }
         }
      },
      Note: {
         keyFields: ['noteId']
      },
      RelatedObject: {
         keyFields: ['relatedEntityId']
      },
      BasePipeline: {
         keyFields: ['pipelineId']
      },
      AgentAllocationGroup: {
         keyFields: ['agentAllocationGroupId']
      },
      BaseUser: {
         keyFields: ['userId', 'name'] //NOTE: Added name for case same userId, different name
      },
      Task: {
         keyFields: ['taskId']
      },
      Lead: {
         keyFields: ['pipelineItemId']
      },
      LeadReminder: {
         keyFields: ['reminderId']
      },
      FieldResponse: {
         keyFields: ['fieldResponseId'],
         fields: {
            response: {
               merge: false
            }
         }
      },
      Parameter: {
         keyFields: ['parameterId']
      },
      GoF: {
         keyFields: ['gofId']
      },
      Field: {
         keyFields: ['fieldId']
      },
      TimeRelativeDefaultValueConfig: {
         keyFields: ['valueEnum']
      },
      DateRelativeDefaultValueConfig: {
         keyFields: ['valueEnum']
      },
      Deal: {
         keyFields: ['pipelineItemId']
      },
      Activity: {
         keyFields: ['activityId']
      },
      ActivityTemplate: {
         keyFields: ['activityTemplateId']
      },
      CallLog: {
         keyFields: ['callLogId']
      },
      PipelineItemHistory: {
         keyFields: ['pipelineItemHistoryId']
      },
      GetCallLogsWithFiltersResult: {
         keyFields: [
            'requestPipelineId',
            'filterByUserId',
            'callStatuses',
            'callStartDatetimeRange',
            'customerPhoneNumber'
         ],
         fields: {
            requestPipelineId: {
               read: (existing, { variables }) =>
                  variables?.['params'].pipelineId
            },
            filterByUserId: {
               read: (existing, { variables }) =>
                  variables?.['params'].filterByUserId ?? ''
            },
            callStatuses: {
               read: (existing, { variables }) =>
                  JSON.stringify(variables?.['params'].callStatuses)
            },
            callStartDatetimeRange: {
               read: (existing, { variables }) =>
                  JSON.stringify(
                     variables?.['params'].callStartDatetimeRange
                  ) ?? ''
            },
            customerPhoneNumber: {
               read: (existing, { variables }) =>
                  JSON.stringify(variables?.['params'].customerPhoneNumber) ??
                  ''
            },
            callLogs: {
               keyArgs: false,
               merge: customFieldMerger
            }
         }
      },
      ScheduledPipelineItemsTeamStatsResult: {
         keyFields: ['dateTimeRange'],
         fields: {
            dateTimeRange: {
               read: (existing, { variables }) =>
                  JSON.stringify(variables?.['params'].dateTimeRange) ?? ''
            },
            userWiseStats: {
               keyArgs: false,
               merge: customFieldMerger
            }
         }
      },
      ScheduledPipelineItemsResult: {
         keyFields: ['dateTimeRange', 'scheduledByUserId', 'statuses'],
         fields: {
            dateTimeRange: {
               read: (existing, { variables }) =>
                  JSON.stringify(variables?.['params'].dateTimeRange) ?? ''
            },
            statuses: {
               read: (existing, { variables }) => {
                  if (variables) {
                     return variables['params'].statuses.length > 0
                        ? variables['params'].statuses.join('')
                        : ''
                  }
               }
            },
            scheduledByUserId: {
               read: (existing, { variables }) =>
                  variables?.['params'].scheduledByUserId
            },
            scheduledPipelineItems: {
               keyArgs: false,
               merge: customFieldMerger
            }
         }
      },
      AutomationWorkflow: {
         keyFields: ['automationWorkflowId']
      },
      PipelineItemReminder: {
         keyFields: ['reminderId']
      },
      PipelineItemRemarks: {
         keyFields: ['pipelineItemRemarksId']
      },
      LeadDistributionRule: {
         keyFields: ['leadDistributionRuleId']
      },
      LeadDistributionRuleExecLog: {
         keyFields: ['leadDistributionRuleExecLogId']
      },
      CSVDataLoadingRequest: {
         keyFields: ['dataLoadingRequestId']
      },
      BasePipelineItemProperties: {
         keyFields: ['pipelineItemId']
      },
      DealProperties: {
         keyFields: ['pipelineItemId']
      },
      LeadProperties: {
         keyFields: ['pipelineItemId']
      },
      Dashboard: {
         keyFields: ['dashboardId']
      },
      UserGroup: {
         keyFields: ['userGroupId']
      },
      AutomationWorkflowExecLog: {
         keyFields: ['automationWorkflowExecLogId']
      },
      CallWebhookNode: {
         keyFields: ['nodeId']
      },
      UpdateLeadNode: {
         keyFields: ['nodeId']
      },
      SendDataToSegmentNode: {
         keyFields: ['nodeId']
      },
      DataExportRequest: {
         keyFields: ['dataExportRequestId']
      },
      FailedTrackActivityLog: {
         keyFields: ['activityLogId']
      },
      BaseActivityTemplate: {
         keyFields: ['referenceId']
      },
      ScheduledPipelineItem: {
         keyFields: ['pipelineItemScheduledId']
      },
      CalendarPipelineItemSchedule: {
         keyFields: ['pipelineItemScheduledId']
      },
      SearchPipelineItemPrimaryDetails: {
         keyFields: ['pipelineItemId']
      },
      User: {
         keyFields: ['userId']
      },
      RoleConfig: {
         keyFields: ['identificationId']
      },
      UserGroupConfig: {
         keyFields: ['identificationId']
      },
      Stage: {
         keyFields: ['stageId']
      },
      SalesTemplateFields: {
         keyFields: ['salesTemplateId']
      },
      SheetDataLoadingRequest: {
         keyFields: ['dataLoadingRequestId']
      },
      IdentificationOverviewDetails: {
         keyFields: ['identificationId']
      },
      ManualLeadDistributionRuleExecLog: {
         keyFields: ['leadDistributionRuleExecLogId']
      },
      EventBasedLeadDistributionRuleExecLog: {
         keyFields: ['leadDistributionRuleExecLogId']
      },
      ScheduledLeadDistributionRuleExecLog: {
         keyFields: ['leadDistributionRuleExecLogId']
      },
      UserCallingConfig: {
         keyFields: ['userId']
      },
      WatiAccount: {
         keyFields: ['pluginServiceAccountId']
      },
      WhatsAppBusinessNumber: {
         keyFields: ['pipelineWhatsappBusinessPhoneNumberId']
      },
      PipelineItemMessageTemplate: {
         keyFields: ['whatsappMessageTemplateId']
      },
      TaskTemplate: {
         keyFields: ['taskTemplateId']
      },
      ScheduledAutomationWorkflowExecLog: {
         keyFields: ['execLogId']
      },
      Notification: {
         keyFields: ['notificationId']
      },
      DocumentTemplate: {
         keyFields: ['documentTemplateId']
      },
      Document: {
         keyFields: ['documentId']
      },
      DocumentVersion: {
         keyFields: ['documentVersionId']
      },
      ScoringRecalculationExecLog: {
         keyFields: ['execLogId']
      },
      SmartfloAccount: {
         keyFields: ['pluginServiceAccountId']
      },
      CloudCallingLog: {
         keyFields: ['callLogId']
      },
      Resource: {
         keyFields: ['resourceId']
      },
      PaymentOrder: {
         keyFields: ['orderId']
      },
      GenerateScrutinyLog: {
         keyFields: ['id']
      },
      DocumentVerificationUser: {
         keyFields: ['userId']
      },
      OfficerLeaveLog: {
         keyFields: ['id']
      },
      OfficerManagementLog: {
         keyFields: ['id']
      },
      SearchAndMonitoringApplicationData: {
         keyFields: ['applicationId']
      }
   }
})

const client = new ApolloClient({
   link,
   cache: cache,
   defaultOptions,
   queryDeduplication: false
})

export const apolloClientConfig = {
   cache: cache,
   defaultOptions,
   queryDeduplication: false
}

export { client }
